import {
  type TooltipModel,
  type ChartDataset,
  type ScriptableTooltipContext,
} from "chart.js";
import { compact, isNil } from "lodash";
import moment from "moment";
import { renderToString } from "react-dom/server";

import { type Kpi } from "@features/mmm/components/explore-budgets/client-setting/types";
import { PacingStatus } from "@features/mmm/components/shared/pacing-status";

import {
  LOWER_MODIFIER,
  UPPER_MODIFIER,
  checkIfClose,
  createPolylineEl,
  getMinMaxOfDatasets,
} from "../chart-utils";

import {
  ACTUAL_COLOR,
  ACTUAL_ID,
  ACTUAL_LEGEND,
  LIVE_FORECAST_COLOR,
  LIVE_FORECAST_ID,
  LIVE_FORECAST_LEGEND,
  PLANNING_TARGET_COLOR,
  PLANNING_TARGET_ID,
  PLANNING_TARGET_LEGEND,
  TARGET_RANGE_COLOR,
  TARGET_RANGE_ID,
} from "./const";

export function chartTooltip(
  context: ScriptableTooltipContext<"line">,
  kpi: Kpi | undefined,
  formatter: (val: number) => string,
) {
  const chart = context.chart;
  const tooltip = context.tooltip;

  if (!kpi || !tooltip.dataPoints.length) {
    return;
  }

  let tooltipEl = document.getElementById("chartjs-tooltip");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    document.body.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (tooltip.body) {
    const { dataIndex, label } = tooltip.dataPoints[0];
    const labels = chart.data.labels;

    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[-30px]",
      center: "translate-x-[50%]",
      right: "translate-x-[30px]",
    }[tooltip.xAlign];

    tooltipEl.innerHTML = `
      <div class="relative">
        <div
          data-tooltip-id="content"
          class="
            bg-v2-primary border border-v2-secondary py-3 px-4 rounded-md
            shadow whitespace-nowrap ${contentPosition} text-sm
            flex items-center space-x-2
          "
        >
        ${buildMiniChartContent(tooltip, dataIndex)}
        <div class="space-y-1">
          <div class="font-bold leading-4">${kpi.friendly_name}</div>
            ${buildDataPointContent(
              chart.data.datasets,
              dataIndex,
              label,
              formatter,
            )}
          </div>
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-v2-primary absolute bottom-[-6px] border-t border-l border-v2-secondary
            ${arrowPosition} rotate-[225deg]
          "
        >
        </div>
      </div>
    `;
  }

  const position = chart.canvas.getBoundingClientRect();

  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: `translate(${translateX}, -100%)`,
    top: `${position.top + window.scrollY + tooltip.caretY}px`,
    left: `${position.left + window.scrollX + tooltip.caretX}px`,
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();

    if (contentRect.left < 0) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("left-0", "translate-x-[-50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[-30px]");
      tooltipEl.style.transform = "translate(0%, -100%)";
    } else if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[40%]",
        "translate-x-[-40%]",
      );
      arrowEl.classList.add("right-0", "translate-x-[40%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[40%]",
      );
      contentEl.classList.add("translate-x-[30px]");
    }
  }
}

function getDatasets(datasets: ChartDataset<any>[], datasetId: string) {
  return datasets.filter((dataset) => {
    return dataset.id === datasetId && !dataset.hidden;
  });
}

const buildMiniChartContent = (
  tooltip: TooltipModel<any>,
  dataIndex: number,
) => {
  const planningTargetDataset = getDatasets(
    tooltip.chart.data.datasets,
    PLANNING_TARGET_ID,
  )?.[0];
  const actualDataset = getDatasets(
    tooltip.chart.data.datasets,
    ACTUAL_ID,
  )?.[0];
  const liveForecastDataset = getDatasets(
    tooltip.chart.data.datasets,
    LIVE_FORECAST_ID,
  )?.[0];
  const targetRangeDatasets = getDatasets(
    tooltip.chart.data.datasets,
    TARGET_RANGE_ID,
  );

  const minMax = getMinMaxOfDatasets(
    compact([
      actualDataset,
      liveForecastDataset,
      planningTargetDataset,
      ...targetRangeDatasets,
    ]),
    dataIndex,
  );

  // Decide the actual is the same as the live forecast
  const actualVal =
    actualDataset?.data?.[dataIndex]?.y ||
    liveForecastDataset?.data?.[dataIndex]?.y;

  const planningTargetVal = planningTargetDataset?.data?.[dataIndex]?.y;
  const isClose = checkIfClose(actualVal, planningTargetVal);

  let planningTargetModifier = 1;
  if (isClose) {
    if (actualVal < planningTargetVal) {
      planningTargetModifier = UPPER_MODIFIER;
    } else {
      planningTargetModifier = LOWER_MODIFIER;
    }
  }

  let actualModifier = 1;
  if (isClose) {
    if (actualVal < planningTargetVal) {
      actualModifier = LOWER_MODIFIER;
    } else {
      actualModifier = UPPER_MODIFIER;
    }
  }
  return `
    <svg viewBox="0 0 100 100" class="h-28 w-28 rounded-md border border-v2-secondary">
      ${
        targetRangeDatasets?.length
          ? createPolylineEl(
              dataIndex,
              minMax,
              targetRangeDatasets,
              planningTargetModifier,
              { fill: TARGET_RANGE_COLOR },
            )
          : ""
      }
      ${
        planningTargetDataset
          ? createPolylineEl(
              dataIndex,
              minMax,
              [planningTargetDataset],
              planningTargetModifier,
              { stroke: PLANNING_TARGET_COLOR },
            )
          : ""
      }
      ${
        liveForecastDataset
          ? createPolylineEl(
              dataIndex,
              minMax,
              [liveForecastDataset],
              actualModifier,
              { stroke: LIVE_FORECAST_COLOR, "stroke-dasharray": "6,2" },
            )
          : ""
      }
      ${
        actualDataset
          ? createPolylineEl(
              dataIndex,
              minMax,
              [actualDataset],
              actualModifier,
              {
                stroke: ACTUAL_COLOR,
              },
            )
          : ""
      }
    </svg>
    `;
};

const buildDataPointContent = (
  datasets: ChartDataset<any>[],
  dataIndex: number,
  label: string,
  formatter: (val: number) => string,
) => {
  const actualDataset = getDatasets(datasets, ACTUAL_ID)?.[0];
  const liveForecastDataset = getDatasets(datasets, LIVE_FORECAST_ID)?.[0];

  const targetMedianDataset = getDatasets(datasets, PLANNING_TARGET_ID)?.[0];
  const actual = actualDataset?.data?.[dataIndex]?.y;
  const liveForecast = liveForecastDataset?.data?.[dataIndex]?.y;
  const budgeted = targetMedianDataset?.data?.[dataIndex]?.y;

  let content = `
    <div class="text-subtitle text-sm flex justify-between items-center pb-3">
    ${moment.utc(label).format("ddd, MMMM DD, YYYY")}
      ${
        actual !== undefined && budgeted !== undefined
          ? renderToString(
              <PacingStatus
                className="px-1 py-0 ml-4"
                style={{ fontSize: "10px" }}
                actual={actual}
                budgeted={budgeted}
              />,
            )
          : ""
      }
    </div>
  `;

  content += createDatasetTooltip(
    actual,
    ACTUAL_LEGEND,
    ACTUAL_ID,
    ACTUAL_COLOR,
    formatter,
  );
  content += createDatasetTooltip(
    liveForecast,
    LIVE_FORECAST_LEGEND,
    LIVE_FORECAST_ID,
    LIVE_FORECAST_COLOR,
    formatter,
  );
  if (!isNil(budgeted)) {
    content += `
      <div class="flex justify-between items-center text-xs">
        <div class="flex items-start mr-4">
          <div class="mr-1 mt-1" style="min-width:33px;">${PLANNING_TARGET_LEGEND}</div>
          <div>
            <div>Forecast</div>
            <div class="italic text-xs text-gray mr-4">Based on average daily figures</div>
          </div>
        </div>
        <div class="font-semibold text-sm" style="color:${PLANNING_TARGET_COLOR}">
          ${formatter(budgeted)}
        </div>
      </div>
    `;
  }

  return content;
};

function createDatasetTooltip(
  value: number,
  title: string,
  subtitle: string,
  color: string,
  formatter: (val: number) => string,
) {
  if (isNil(value)) {
    return "";
  }

  return `
 <div class="flex justify-between items-center text-xs space-x-2">
        <div class="flex items-center space-x-1">
          <div>${title}</div>
          <div>${subtitle}</div>
        </div>
        <div class="font-semibold text-sm" style="color:${color}">
          ${formatter(value)}
        </div>
      </div>
  `;
}
